/**
 * Duplicate Detection Utility
 *
 * Multi-level matching system to detect duplicate students:
 * - Level 1 (Exact): Name + BirthDate match → BLOCK
 * - Level 2 (Strong): Email match OR Name + Phone match → BLOCK
 * - Level 3 (Warning): Name only match → WARN (possible siblings)
 */

/**
 * Normalize string for comparison
 */
const normalize = (str) => (str || "").toLowerCase().trim();

/**
 * Check if two students are duplicates
 * @returns {Object} { isDuplicate: boolean, level: string, reason: string, matchedFields: array }
 */
export const checkDuplicate = (newStudent, existingStudent) => {
  const newFirst = normalize(newStudent.firstName);
  const newLast = normalize(newStudent.lastName);
  const newEmail = normalize(newStudent.email);
  const newPhone = normalize(newStudent.phone);
  const newBirth = normalize(newStudent.birthDate);

  const exFirst = normalize(existingStudent.firstName);
  const exLast = normalize(existingStudent.lastName);
  const exEmail = normalize(existingStudent.email);
  const exPhone = normalize(existingStudent.phone);
  const exBirth = normalize(existingStudent.birthDate);

  const matchedFields = [];

  // Check individual field matches
  const firstNameMatch = newFirst && exFirst && newFirst === exFirst;
  const lastNameMatch = newLast && exLast && newLast === exLast;
  const birthDateMatch = newBirth && exBirth && newBirth === exBirth;
  const emailMatch = newEmail && exEmail && newEmail === exEmail;
  const phoneMatch = newPhone && exPhone && newPhone === exPhone;

  if (firstNameMatch) matchedFields.push("Vorname");
  if (lastNameMatch) matchedFields.push("Nachname");
  if (birthDateMatch) matchedFields.push("Geburtsdatum");
  if (emailMatch) matchedFields.push("E-Mail");
  if (phoneMatch) matchedFields.push("Telefon");

  // LEVEL 1: Exact Match (Name + BirthDate) - STRONGEST INDICATOR
  // This is the ONLY reliable way to confirm same person
  if (firstNameMatch && lastNameMatch && birthDateMatch) {
    return {
      isDuplicate: true,
      level: "exact",
      reason: "Name und Geburtsdatum stimmen überein",
      matchedFields,
      confidence: "high",
    };
  }

  // LEVEL 2: Email Match + Name Match - VERY LIKELY DUPLICATE
  // Email alone is NOT enough (families share emails!)
  // But email + full name = very likely same person
  if (emailMatch && firstNameMatch && lastNameMatch) {
    return {
      isDuplicate: true,
      level: "strong",
      reason: "Name und E-Mail-Adresse stimmen überein",
      matchedFields,
      confidence: "high",
    };
  }

  // LEVEL 3: Name + Phone Match - VERY LIKELY DUPLICATE
  if (firstNameMatch && lastNameMatch && phoneMatch) {
    return {
      isDuplicate: true,
      level: "strong",
      reason: "Name und Telefonnummer stimmen überein",
      matchedFields,
      confidence: "high",
    };
  }

  // LEVEL 4: Family Member Detection (WARNING)
  // Same contact info (email/phone/address) + same lastName BUT different firstName
  // This is likely parent + child or siblings
  if (lastNameMatch && !firstNameMatch && (emailMatch || phoneMatch)) {
    return {
      isDuplicate: false,
      level: "family",
      reason: "Gleicher Nachname und Kontaktdaten (möglicherweise Eltern/Kind oder Geschwister)",
      matchedFields,
      confidence: "low",
    };
  }

  // LEVEL 5: Name Only Match - POSSIBLE SIBLINGS (WARNING)
  if (firstNameMatch && lastNameMatch) {
    return {
      isDuplicate: false,
      level: "possible",
      reason: "Nur Name stimmt überein (möglicherweise Geschwister)",
      matchedFields,
      confidence: "low",
    };
  }

  // LEVEL 5: No match
  return {
    isDuplicate: false,
    level: "none",
    reason: "",
    matchedFields: [],
    confidence: "none",
  };
};

/**
 * Calculate duplicate score using a points-based system
 * @returns {Object} { score, matches, isDuplicate, isWarning }
 */
export const calculateDuplicateScore = (newStudent, existingStudent) => {
  let score = 0;
  const matches = [];

  const newFirst = normalize(newStudent.firstName);
  const newLast = normalize(newStudent.lastName);
  const newEmail = normalize(newStudent.email);
  const newPhone = normalize(newStudent.phone);
  const newBirth = normalize(newStudent.birthDate);
  const newAddress = normalize(newStudent.adress);

  const exFirst = normalize(existingStudent.firstName);
  const exLast = normalize(existingStudent.lastName);
  const exEmail = normalize(existingStudent.email);
  const exPhone = normalize(existingStudent.phone);
  const exBirth = normalize(existingStudent.birthDate);
  const exAddress = normalize(existingStudent.adress);

  // Email match (50 points) - most unique
  if (newEmail && exEmail && newEmail === exEmail) {
    score += 50;
    matches.push("E-Mail");
  }

  // BirthDate match (40 points) - very specific
  if (newBirth && exBirth && newBirth === exBirth) {
    score += 40;
    matches.push("Geburtsdatum");
  }

  // Phone match (30 points) - usually unique per family
  if (newPhone && exPhone && newPhone === exPhone) {
    score += 30;
    matches.push("Telefon");
  }

  // LastName match (30 points) - important but not unique
  if (newLast && exLast && newLast === exLast) {
    score += 30;
    matches.push("Nachname");
  }

  // FirstName match (20 points) - less unique
  if (newFirst && exFirst && newFirst === exFirst) {
    score += 20;
    matches.push("Vorname");
  }

  // Address match (10 points) - siblings share
  if (newAddress && exAddress && newAddress === exAddress) {
    score += 10;
    matches.push("Adresse");
  }

  // Score interpretation:
  // 90+ = Definite duplicate (block import)
  // 60-89 = Likely duplicate (strong warning)
  // 50-59 = Possible duplicate (warning)
  // <50 = Not a duplicate

  return {
    score,
    matches,
    isDuplicate: score >= 90,
    isWarning: score >= 50 && score < 90,
    level:
      score >= 90
        ? "exact"
        : score >= 60
        ? "strong"
        : score >= 50
        ? "possible"
        : "none",
  };
};

/**
 * Find all duplicates for a new student against existing students
 */
export const findDuplicates = (newStudent, existingStudents) => {
  const duplicates = [];

  for (const existing of existingStudents) {
    const result = checkDuplicate(newStudent, existing);
    if (result.isDuplicate) {
      duplicates.push({
        existingStudent: existing,
        ...result,
      });
    }
  }

  return duplicates;
};

/**
 * Check duplicates for an array of new students
 * Returns both duplicates against DB and within the import batch
 */
export const checkAllDuplicates = (newStudents, existingStudents) => {
  const duplicatesAgainstDB = [];
  const duplicatesWithinBatch = [];
  const warnings = [];

  // Check each new student against existing DB
  newStudents.forEach((newStudent, index) => {
    // Check against existing students in DB
    for (const existing of existingStudents) {
      const result = checkDuplicate(newStudent, existing);

      if (result.isDuplicate) {
        duplicatesAgainstDB.push({
          newStudent,
          existingStudent: existing,
          rowIndex: index,
          ...result,
        });
      } else if (result.level === "possible" || result.level === "family") {
        warnings.push({
          newStudent,
          existingStudent: existing,
          rowIndex: index,
          ...result,
        });
      }
    }

    // Check within the import batch (for duplicates in CSV itself)
    for (let j = index + 1; j < newStudents.length; j++) {
      const other = newStudents[j];
      const result = checkDuplicate(newStudent, other);

      if (result.isDuplicate) {
        duplicatesWithinBatch.push({
          student1: newStudent,
          student2: other,
          row1: index,
          row2: j,
          ...result,
        });
      }
    }
  });

  return {
    duplicatesAgainstDB,
    duplicatesWithinBatch,
    warnings,
    hasDuplicates:
      duplicatesAgainstDB.length > 0 || duplicatesWithinBatch.length > 0,
    hasWarnings: warnings.length > 0,
  };
};
